import * as Sentry from '@sentry/react';
import { T, Var } from 'gt-react';
import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { Button } from 'src/components/ui/button';
import { isNetworkError } from 'src/integrations/sentry/is-network-error';

import { AuthErrorMessage } from './AuthErrorMessage';
import { AuthShell } from './AuthShell';
import { Spinner } from 'src/components/ui/spinner';

interface AuthServiceErrorStateProps {
  description?: ReactNode;
  error?: Error | unknown;
  isRetrying?: boolean;
  onRetry: () => void;
  title?: ReactNode;
}

export function AuthServiceErrorState({
  description,
  error,
  isRetrying = false,
  onRetry,
  title,
}: AuthServiceErrorStateProps) {
  useEffect(() => {
    const message = error instanceof Error ? error.message : '';
    if (error && !isNetworkError(message)) {
      Sentry.captureException(
        error instanceof Error ? error : new Error('Backend unreachable'),
        { tags: { context: 'auth_service_error' } },
      );
    }
  }, [error]);

  return (
    <AuthShell title={title} description={description} washOut={false}>
      <div className="space-y-4 py-2">
        <AuthErrorMessage error={toDisplayError(error)} />

        <p className="text-center text-gray-500 text-sm font-light leading-relaxed">
          <T context="support message shown on the auth service unavailable page">
            Contact <Var>support@gently-ai.com</Var> if this issue persists.
          </T>
        </p>

        <Button className="w-full" onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? (
            <Spinner className="mx-auto text-white" />
          ) : (
            <T context="retry button label on the auth service unavailable page">
              Retry
            </T>
          )}
        </Button>
      </div>
    </AuthShell>
  );
}

function toDisplayError(error?: Error | unknown) {
  if (!error) {
    return undefined;
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error('Unable to reach the server.');
}
